import React, { useMemo, useEffect, useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Box, 
  Container, 
  useMediaQuery, 
  useTheme,
  BottomNavigation,
  BottomNavigationAction
} from '@mui/material';
import Logo from './components/Logo';
import AppTheme from './style/AppTheme';
import { 
  IconUserPlus, 
  IconUser, 
  IconCirclePlus, 
  IconUserCog, 
  IconBookmarks, 
  IconDatabaseCog,
  IconMessage2
} from '@tabler/icons-react';
import { useAuth } from './components/AuthProvider';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import ResponsiveButton from './components/ResponsiveButton';
import Footer from './pages/Footer';
import CookieBanner from './components/CookieBanner';
/**
 * Main App layout component
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
function App({ children }) {
  const { isLoggedIn, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [bottomNavValue, setBottomNavValue] = useState(0);

  // Log authentication state for debugging
  useEffect(() => {
    console.log('App authentication state:', { isLoggedIn, userRole });
  }, [isLoggedIn, userRole]);

  // Update bottom nav based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') setBottomNavValue(0);
    else if (path === '/admin/cache' || path.startsWith('/admin/')) setBottomNavValue(1);
    else if (path === '/user/listing/create') setBottomNavValue(isLoggedIn && userRole === 'ADMIN' ? 2 : 1);
    else if (path === '/user/favorite') setBottomNavValue(isLoggedIn && userRole === 'ADMIN' ? 3 : 2);
    else if (path === '/user/listing') setBottomNavValue(isLoggedIn && userRole === 'ADMIN' ? 4 : 3);
    else if (path === '/user/account') setBottomNavValue(isLoggedIn && userRole === 'ADMIN' ? 5 : 4);
    else setBottomNavValue(-1);
  }, [location.pathname, isLoggedIn, userRole]);

  // Memoize navigation handlers to prevent unnecessary re-renders
  const handleRegisterClick = useMemo(() => () => navigate("/sign/up"), [navigate]);
  const handleLoginClick = useMemo(() => () => navigate("/sign/in"), [navigate]);
  const handleMyListingsClick = useMemo(() => () => navigate("/user/listing"), [navigate]);
  const handleFavoritesClick = useMemo(() => () => navigate("/user/favorite"), [navigate]);
  const handleAddListingClick = useMemo(() => () => navigate("/user/listing/create"), [navigate]);
  const handleAdminPanelClick = useMemo(() => () => navigate("/admin/cache"), [navigate]);
  const handleMyAccountClick = useMemo(() => () => navigate("/user/account"), [navigate]);
  const handleMessagesClick = useMemo(() => () => navigate("/user/messages"), [navigate]);

  const handleBottomNavChange = (event, newValue) => {
    setBottomNavValue(newValue);
    switch(newValue) {
      case 0: navigate("/"); break;
      case 1: 
        if (isLoggedIn && userRole === 'ADMIN') {
          navigate("/admin/cache");
        } else if (isLoggedIn) {
          navigate("/user/listing/create");
        }
        break;
      case 2: 
        if (isLoggedIn && userRole === 'ADMIN') {
          navigate("/user/listing/create");
        } else if (isLoggedIn) {
          navigate("/user/favorite");
        }
        break;
      case 3: 
        if (isLoggedIn && userRole === 'ADMIN') {
          navigate("/user/favorite");
        } else if (isLoggedIn) {
          navigate("/user/listing");
        }
        break;
      case 4: 
        if (isLoggedIn && userRole === 'ADMIN') {
          navigate("/user/listing");
        } else if (isLoggedIn) {
          navigate("/user/account");
        }
        break;
      case 5:
        if (isLoggedIn && userRole === 'ADMIN') {
          navigate("/user/account");
        }
        break;
      default: break;
    }
  };

  // Handle login button click
  const handleLoginLogoutClick = useMemo(() => {
    return () => navigate("/sign/in");
  }, [navigate]);

  return (
    <AppTheme>
      {/* Skip link for accessibility */}
      <Box sx={{ position: 'absolute', left: '-9999px', top: 0, zIndex: 9999 }}>
        <a href="#main-content" tabIndex={0}>
          Przejdź do treści głównej
        </a>
      </Box>

      {/* Alert Dialog is now handled at the root level in index.js */}

      {/* App header */}
      <AppBar
        position="sticky"
        sx={{ 
          width: "100%", 
          bgcolor: "info.main",
          color: "info.contrastText",
          boxShadow: '0 1px 2px rgba(30, 26, 22, 0.06), 0 8px 18px rgba(30, 26, 22, 0.06)'
        }}
        component="header"
        aria-label="Nagłówek strony"
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <Box sx={{ flexGrow: 1 }}>
            <Logo />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box component="nav" aria-label="Nawigacja główna">
              {!isLoggedIn ? (
                <>
                  <ResponsiveButton
                    sx={{ m: 1, color: "info.contrastText" }}
                    label="Zarejestruj"
                    icon={<IconUserPlus aria-hidden="true" />}
                    onClick={handleRegisterClick}
                    aria-label="Zarejestruj się"
                  />
                  <ResponsiveButton
                    sx={{ m: 1, color: "info.contrastText" }}
                    label="Zaloguj"
                    icon={<IconUser aria-hidden="true" />}
                    onClick={handleLoginClick}
                    aria-label="Zaloguj się"
                  />
                </>
              ) : (
                <Box>
                  <ResponsiveButton
                    sx={{ m: 1, color: "info.contrastText" }}
                    label="Moje ogłoszenia"
                    icon={<IconUserCog aria-hidden="true" />}
                    onClick={handleMyListingsClick}
                    aria-label="Przejdź do moich ogłoszeń"
                  />
                  <ResponsiveButton
                    sx={{ m: 1, color: "info.contrastText" }}
                    label="Moje konto"
                    icon={<IconUser aria-hidden="true" />}
                    onClick={handleMyAccountClick}
                    aria-label="Przejdź do mojego konta"
                  />
                  <ResponsiveButton
                    sx={{ m: 1, color: "info.contrastText" }}
                    label="Wiadomości"
                    icon={<IconMessage2 aria-hidden="true" />}
                    onClick={handleMessagesClick}
                    aria-label="Przejdź do wiadomości"
                  />
                  <ResponsiveButton
                    sx={{ m: 1, color: "info.contrastText" }}
                    label="Ulubione"
                    icon={<IconBookmarks aria-hidden="true" />}
                    onClick={handleFavoritesClick}
                    aria-label="Przejdź do ulubionych ogłoszeń"
                  />
                  <ResponsiveButton
                    sx={{ m: 1, color: "info.contrastText" }}
                    label="Dodaj ogłoszenie"
                    icon={<IconCirclePlus aria-hidden="true" />}
                    onClick={handleAddListingClick}
                    aria-label="Dodaj nowe ogłoszenie"
                  />
                  {/* Check if user has ADMIN role and show admin panel button */}
                  {userRole === 'ADMIN' && (
                    <ResponsiveButton
                      sx={{ m: 1, color: "#fff", bgcolor: "error.main" }}
                      label="Panel Admina"
                      icon={<IconDatabaseCog aria-hidden="true" />}
                      onClick={handleAdminPanelClick}
                      aria-label="Przejdź do panelu administratora"
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Container
        component="main"
        id="main-content"
        maxWidth="xl"
        sx={{ 
          p: { xs: 1, sm: 2 },
          pb: { xs: 10, sm: 10 , md: 2 }, // Space for bottom nav on mobile
          minHeight: 'calc(100vh - 64px)'
        }}
        tabIndex={-1}
      >
        {children}
      </Container>
            <Footer/>
        
      {/* Cookie Consent Banner */}
      <CookieBanner />
        
      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <BottomNavigation
          value={bottomNavValue}
          onChange={handleBottomNavChange}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
          }}
        >
          {isLoggedIn && userRole === 'ADMIN' && (
            <BottomNavigationAction 
              component={Link}
              to="/admin/cache"
              label="Panel Admina" 
              icon={<IconDatabaseCog />} 
              aria-label="Panel administratora"
            />
          )}
          {isLoggedIn && (
            <BottomNavigationAction 
              component={Link}
              to="/user/listing/create"
              label="Dodaj" 
              icon={<IconCirclePlus />} 
              aria-label="Dodaj ogłoszenie"
            />
          )}
          {isLoggedIn && (
            <BottomNavigationAction 
              component={Link}
              to="/user/favorite"
              label="Ulubione" 
              icon={<IconBookmarks />} 
              aria-label="Ulubione ogłoszenia"
            />
          )}
          {isLoggedIn && (
            <BottomNavigationAction 
              component={Link}
              to="/user/listing"
              label="Moje" 
              icon={<IconUserCog />} 
              aria-label="Moje ogłoszenia"
            />
          )}
          {isLoggedIn && (
            <BottomNavigationAction 
              component={Link}
              to="/user/account"
              label="Konto" 
              icon={<IconUser />} 
              aria-label="Moje konto"
            />
          )}
          {!isLoggedIn && (
            <BottomNavigationAction 
              onClick={handleLoginLogoutClick}
              label="Zaloguj" 
              icon={<IconUser />} 
              aria-label="Zaloguj się"
            />
          )}
        </BottomNavigation>
      )}
    </AppTheme>
  );
}

export default App;
